import React from 'react';

import { handleMissingProductImage } from '../products/product-utils';
import { fetchProductDetail } from '../dispensaries/dispensary-utils';
import BasicButton from '../styleguide/BasicButton'; 
import QuantityIcon from '../checkout/QuantityIcon';
import ErrorIcon from '@material-ui/icons/Error';

import PropTypes from 'prop-types';

import styles from './ReOrder.module.css';

const ReOrderItem = ({
  Id,
  dispensaryId,
  name,
  image_url,
  category,
  cost_usa_cents,
  quantity,
  leaf_logix_pricing_tier, /* TODO: Show bulk discount icon */
  productFlowerGrams,
  totalFlowerGrams,
  handleQuantityChange,
  cartThisItem=false,  /* triggered via button on parent component */
  addToCart
}) => {

  // Handle missing product image
  const { imgUrl:productImage } = handleMissingProductImage(image_url, category.display_name);

  // Quantities are updated locally and then added to cart   
  const [reorderQuantity, setReorderQuantity] = React.useState(quantity);
  // const [isRemoved, setIsRemoved] = React.useState();
  const [quantityAvailable, setQuantityAvailable] = React.useState();
  const [displayPrice, setDisplayPrice] = React.useState(0); 

  // Update Price/Quantity if/when adjusted
  React.useEffect(() => {
    setDisplayPrice(parseFloat(reorderQuantity * cost_usa_cents / 100).toFixed(2));
  }, [reorderQuantity, cost_usa_cents]); 

  // Only do the availability check once
  const availabilityRef = React.useRef();
  const [notAvailable, setNotAvailable] = React.useState();
  const controllerRef = React.useRef(new AbortController());

  // Fetch available product quantity 
  React.useEffect(() => {
    const controller = controllerRef.current;
    const callback = (response) => {
      // only check once
      availabilityRef.current = true;
      setQuantityAvailable(response.quantity_available || 0);
    };
    if (!availabilityRef.current) {
      fetchProductDetail(Id, dispensaryId, callback, controller);
    } 
    return () => controller.abort();  
  }, [Id, dispensaryId]);

  // Monitor order quantity versus available
  React.useEffect(() => {
    const isNotAvailable = quantityAvailable === 0 ||
                           quantityAvailable < reorderQuantity;  
    setNotAvailable(isNotAvailable);
  }, [quantityAvailable, reorderQuantity]);

  // Cart items when reorder is submitted
  React.useEffect(() => {
    if(cartThisItem && reorderQuantity > 0) {
      addToCart(reorderQuantity);    
    }
  }, [addToCart,cartThisItem,reorderQuantity]);

  const adjustQuantity = (adjustCount) => {
    const newQuantity = reorderQuantity + adjustCount;
    const itemInfo = {
      price: cost_usa_cents,
      flower_equivalent_grams: productFlowerGrams,
      quantity: newQuantity  
    }
    handleQuantityChange(Id, itemInfo);
    setReorderQuantity(newQuantity);     
  };

  const itemRef = React.useRef();

  const toggleItem = (isRestore) => {
    if (itemRef.current) {
      itemRef.current.setAttribute('data-is-removed', !isRestore);
    }
    const newQuantity = isRestore ? 1 : 0;
    const itemInfo = {
      price: cost_usa_cents,
      flower_equivalent_grams: productFlowerGrams,
      quantity: newQuantity  
    }
    handleQuantityChange(Id, itemInfo);
    setReorderQuantity(newQuantity);    
  } 
  
  const notAvailableIconStyle = {
    fontSize: '22px', 
    verticalAlign: '-5px', 
    color: '#d80303' 
  }

  return (
    <li ref={itemRef} className={styles.reorderItem} data-is-removed={false} key={Id}>
      <div className={styles.itemDetails}>
        <span className={styles.itemCount}>x{reorderQuantity}</span>
        <span className={styles.itemTitle}>
          {name} 
        </span>
        <span className={styles.itemPriceCol}>
          <span className={styles.itemPrice}>
            ${displayPrice}
          </span>
          <span className={styles.restoreButton}>
            <BasicButton text="Restore Item" 
              withStyle={{fontSize:'.875rem', padding:'.25rem .5rem'}} 
              handleClick={() => toggleItem(true)} />
          </span>  
        </span>
      </div>
      
      <div className={styles.imageWrap}>
        <img src={productImage} alt={name} className={styles.itemImg}/>
        { reorderQuantity > 0 &&
          <QuantityIcon cartCount={reorderQuantity} productFlowerGrams={productFlowerGrams} totalFlowerGrams={totalFlowerGrams} />
        } 
        <span className={`${styles.itemControl} ${styles.increase}`} onClick={
            () => adjustQuantity(1) 
          }>+</span>
        { reorderQuantity > 0 &&
          <span className={`${styles.itemControl} ${styles.decrease}`} 
                onClick={ () => adjustQuantity(-1) }>-</span>
        }   
      </div>
 
      <div className={styles.btmRow}>
        <div className={styles.inventoryAlert}>
          {/* Show message about limited quantities */}
          { quantityAvailable < 10 &&  
            <>Limited quantity: Only {quantityAvailable} available.</>
          }
        </div>
        <div className={styles.itemRemove}>
          <BasicButton text="Remove Item" 
            withStyle={{fontSize:'.875rem', padding:'.25rem .5rem'}} 
            handleClick={() => toggleItem()} />
        </div>
      </div>

      { notAvailable && 
        <>
          <div className={styles.notAvailableWrap}>
            <ErrorIcon style={notAvailableIconStyle} /> 
            { quantityAvailable === 0
              ? <>{' '}This product is not currently available</>
              : <>{' '}There are only <b>{quantityAvailable}</b> available.</>
            }  
          </div>
        </>        
      }
    </li>
  )
}

ReOrderItem.propTypes = {
  Id: PropTypes.string.isRequired,
  dispensaryId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  image_url: PropTypes.string, /* we handle missing product images */
  category: PropTypes.object.isRequired,
  cost_usa_cents: PropTypes.number.isRequired,
  quantity: PropTypes.number.isRequired,
  leaf_logix_pricing_tier: PropTypes.object, /* optional - has bulk discount */
  productFlowerGrams: PropTypes.number.isRequired,
  totalFlowerGrams: PropTypes.number.isRequired,
  handleQuantityChange: PropTypes.func.isRequired,
  cartThisItem: PropTypes.bool.isRequired,  /* triggered via button on parent component */
  addToCart: PropTypes.func.isRequired
};

export default ReOrderItem;